import { BasketItemFlags } from '@virginexperiencedays/basket/src/utils/basketItemFlags';
import { QuickBasket } from '@virginexperiencedays/components-v2/src/drawers/QuickBasket';
import {
  type Props as QuickBasketContextProps,
  QuickBasketContextProvider,
} from '@virginexperiencedays/components-v2/src/drawers/QuickBasket/utils/QuickBasketContext';
import { Container } from '@virginexperiencedays/components-v2/src/layout/Container';
import { useModal } from '@virginexperiencedays/components-v2/src/layout/Modal/useModal';
import { Stack } from '@virginexperiencedays/components-v2/src/layout/Stack';
import { ExperienceSummary } from '@virginexperiencedays/components-v2/src/pages/product/ExperienceSummary';
import { HeroImages } from '@virginexperiencedays/components-v2/src/pages/product/HeroImages';
import { HeroTitle } from '@virginexperiencedays/components-v2/src/pages/product/HeroTitle';
import { Locations } from '@virginexperiencedays/components-v2/src/pages/product/Locations';
import { MoreFromCategory } from '@virginexperiencedays/components-v2/src/pages/product/MoreFromCategory';
import { ProductChoices } from '@virginexperiencedays/components-v2/src/pages/product/ProductChoices';
import { ProductMetaDetails } from '@virginexperiencedays/components-v2/src/pages/product/ProductMetaDetails';
import { ProductOverview } from '@virginexperiencedays/components-v2/src/pages/product/ProductOverview';
import { ProductPriceDetails } from '@virginexperiencedays/components-v2/src/pages/product/ProductPriceDetails';
import { ProductUsps } from '@virginexperiencedays/components-v2/src/pages/product/ProductUsps';
import { roundStarRating } from '@virginexperiencedays/components-v2/src/utils/productRating';
import { DyContextPageType } from '@virginexperiencedays/dy';
import { useScrollLock } from '@virginexperiencedays/hooks';
import useIntersectionObserver from '@virginexperiencedays/hooks/src/useIntersectionObserver';
import { slugToName } from '@virginexperiencedays/search/utils/category';
import { getCookieMap } from '@virginexperiencedays/web/utils/cookie';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { LOYALTY_KLAVIYO_EXTERNAL_ID_COOKIE } from '../../../constants/cookie';
import { trackGrc } from '../../../libs/tracking/grc';
import { GrcCustomerState, GrcInteraction, GrcPageType } from '../../../libs/tracking/types';
import { initAlgoliaTrackingClient } from '../../../utils/algolia/tracking';
import { isB2bProduct } from '../../../utils/b2b/isB2bProduct';
import { useValidB2bRoute } from '../../../utils/b2b/useValidB2bRoute';
import Breadcrumbs from '../../Breadcrumbs';
import DynamicYield from '../../DynamicYield/DynamicYield';
import OthersWentOnToBuy from '../../DynamicYield/OthersWentOnToBuy';
import LocationsMap from '../../LocationsMap/LocationsMap';
import { breadcrumbs } from '../../SEO/StructuredData/breadcrumbs';
import { addToDataLayer } from '../../tracking/gtm/utils';
import { trackRemovedFromCart as trackKlaviyoRemovedFromCart } from '../../tracking/klaviyo';
import { useHandleUpdateBasketCount } from '../hooks/useHandleUpdateBasketCount';
import AddToBasket from './AddToBasket';
import Klarna from './Klarna';
import type { PageProps } from './Page';
import { Reviews } from './Reviews';
import Seo from './SEO';
import Stripe from './Stripe';
import StructuredData from './StructuredData';
import { useRecentlyViewed } from './hooks/useRecentlyViewed';

// Dynamic Imports
const HeroImageViewer = dynamic(() => import('./HeroImageViewer'), {
  ssr: false,
});

/**
 * The entire PageUI component is reused by React when moving across PDPs,
 * causing some state to persist (e.g. View All Photos modal staying open,
 * previous product data flashing).
 *
 * To force React to re-render a new component per page, the whole component is
 * keyed with router.asPath using a wrapper.
 */
const Page = (props: PageProps) => {
  const router = useRouter();
  return <PageUI key={router.asPath} {...props} />;
};

const PageUI = ({
  product,
  reviews: individualReviews,
  isActive,
  productPromo,
  collectionSlug,
  slugNameMap,
  slugBreadcrumbNameMap,
}: PageProps) => {
  const [quickBasketOpen, setQuickBasketOpen] = useState(false);

  // B2B Sales Reporting
  const { visitedValidRoutes } = useValidB2bRoute();
  const isB2b = isB2bProduct(product) && visitedValidRoutes();
  const basketItemFlags: BasketItemFlags[] = isB2b ? [BasketItemFlags.REPORT_AS_B2B] : undefined;

  initAlgoliaTrackingClient();

  // Refs
  const reviewsRef: MutableRefObject<HTMLDivElement | undefined> = useRef();
  const locationsMapRef: MutableRefObject<HTMLDivElement | undefined> = useRef();
  const whatsIncludedRef: MutableRefObject<HTMLDivElement | undefined> = useRef();
  const router = useRouter();

  // Hooks
  const { isOpen: isModalOpen, toggleModal } = useModal();
  const { unlockScroll } = useScrollLock([isModalOpen, quickBasketOpen]);

  const handleUpdateBasketCount = useHandleUpdateBasketCount();

  // iOS bfcache causes issues with the Quick Basket state when navigating back using the browser back button
  // this hook causes a page refresh when the user navigates back to the page, utilising the pageshow event
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const forceRefresh = (e: PageTransitionEvent) => {
      if (e.persisted) location.reload();
    };

    window.addEventListener('pageshow', forceRefresh);
    return () => {
      unlockScroll(); // ensure scroll is restored on unmount
      window.removeEventListener('pageshow', forceRefresh);
    };
  }, [unlockScroll]);

  // Intersection observer
  const options = { freezeOnceVisible: true, deps: [router.asPath] };
  const locationsEntry = useIntersectionObserver(locationsMapRef, options);
  const additionalDetailsEntry = useIntersectionObserver(whatsIncludedRef, options);

  const collectionUrl = `/collection/${collectionSlug}`;

  // Effect: updates recently viewed products
  useRecentlyViewed({ ...product, productPromo });

  const {
    categories = [],
    categoryHierarchy,
    deliveryOptions,
    description,
    id,
    info: { whatsIncluded, texts },
    isCollectionProduct,
    isCollectionChoice,
    productChoices,
    locations = [],
    media: { mainImage, images },
    numberOfPeople,
    price,
    promocode,
    reviews: productReviews,
    sku,
    slug,
    stock,
    unitsAvailableIsPublic,
    title,
    validity,
  } = product;
  const { lvl0, lvl1, lvl2 } = categoryHierarchy ?? {};
  const getSlugName = (slug: string) => slugNameMap?.[slug] ?? slugToName(slug);

  const handleClickBackToCollection = useCallback(() => {
    // let original event propagate

    // tracking
    addToDataLayer({
      event: 'userInteraction',
      eventCategory: 'Collection product',
      eventAction: `Clicked back to collection | ${slug}`,
      eventLabel: 'button',
    });
  }, [slug]);

  const handleClickProductChoice = useCallback(
    ({ title }) => {
      // let original event propagate

      // tracking
      addToDataLayer({
        event: 'userInteraction',
        eventCategory: 'Collection product',
        eventAction: `Clicked product | ${slug}`,
        eventLabel: title,
      });
    },
    [slug],
  );

  const handleQuickBasketOpen = (isOpen: boolean) => {
    setQuickBasketOpen(isOpen);
  };

  const isCollectionPages = isCollectionProduct || isCollectionChoice;
  const seoPathOverride = isCollectionChoice
    ? `${collectionUrl}/product/${slug}`
    : `/collection/${slug}`;

  // Apply rating logic to decide whether to render Star Rating and Customer Reviews or not
  const roundedRating = roundStarRating(productReviews?.averageRating);
  const reviews = roundedRating ? productReviews : null;
  const reviewsList = roundedRating ? individualReviews : null;

  const [hasGcContactId, setHasGcContactId] = useState<boolean>(false);

  useEffect(() => {
    if (process?.env?.NEXT_PUBLIC_FF_LOYALTY) {
      const cookieMap = getCookieMap();
      const contactId = cookieMap?.get(LOYALTY_KLAVIYO_EXTERNAL_ID_COOKIE);

      setHasGcContactId(!!contactId);
    }
  }, []);

  const handleLoyaltyClicks = (interaction: GrcInteraction) => {
    trackGrc({
      customerState: GrcCustomerState.Valid,
      interaction,
      pageType: GrcPageType.Pdp,
    });
  };

  const handleCallbackSetBasketItems: QuickBasketContextProps['callbackSetBasketItems'] = ({
    removedItem,
    basketItems,
    totalAmount,
  }) => {
    // track removed item from basket
    if (!removedItem) return;

    trackKlaviyoRemovedFromCart({
      categories: removedItem.categories,
      displayPrice: removedItem.displayPrice,
      imageUrl: removedItem.image?.src,
      qty: removedItem.qty,
      rrp: removedItem.rrp,
      sku: removedItem.sku,
      slug: removedItem.slug,
      title: removedItem.title,
      totalAmount: totalAmount ?? 0,
      url: removedItem.url,
      isCollectionProduct: removedItem.isCollectionProduct,
      items:
        basketItems?.map(
          ({ discountedPrice, displayPrice, image: { src: imageUrl }, ...item }) => ({
            ...item,
            displayPrice: discountedPrice ?? displayPrice ?? 0,
            imageUrl,
          }),
        ) ?? [],
    });
  };

  if (router.isFallback) return <div>Loading ...</div>;

  return (
    <>
      <Seo
        product={product}
        pagePathOverride={isCollectionPages ? seoPathOverride : undefined}
        metaRobots={isCollectionChoice ? 'noindex' : undefined}
      />
      <DynamicYield
        type={DyContextPageType.PRODUCT}
        data={[promocode ? `${sku}_${promocode}` : sku]}
      />
      <StructuredData
        product={product}
        reviews={reviewsList}
        pagePathOverride={isCollectionPages ? seoPathOverride : undefined}
        hideReviews={isCollectionProduct}
        hideOffers={isCollectionChoice}
      />

      <QuickBasketContextProvider
        setQuickBasketOpen={setQuickBasketOpen}
        handleQuickBasketItemCount={(n) => handleUpdateBasketCount(n)}
        callbackSetBasketItems={handleCallbackSetBasketItems}
      >
        <QuickBasket
          isOpen={quickBasketOpen}
          handleIsOpen={handleQuickBasketOpen}
          hasLoyaltyCta={hasGcContactId}
          onLoyaltyCheckoutClick={() => handleLoyaltyClicks(GrcInteraction.CheckoutNow)}
          onLoyaltyEditBasketClick={() => handleLoyaltyClicks(GrcInteraction.EditBasket)}
          onLoyaltyBannerClick={() => handleLoyaltyClicks(GrcInteraction.LoyaltyBanner)}
        />
        <Container>
          <Breadcrumbs
            asExternalLinks
            crumbs={[
              ...breadcrumbs(lvl2 || lvl1 || lvl0, slugBreadcrumbNameMap),
              { name: product.title },
            ]}
          />
        </Container>

        <Container mobilePadding={false}>
          <div className="block md:flex md:gap-6 lg:gap-14">
            <div className="h-fit flex-1 shrink-0 basis-1/2 md:sticky md:top-6">
              <HeroImages
                className="mb-4 md:m-0"
                productName={title}
                images={[mainImage, ...images]}
                onClick={toggleModal}
                price={price}
                productPromo={productPromo}
              />
              {/* For Dynamic Yield injection */}
              <div id="dy-pdp-recs" />
            </div>
            {isModalOpen && (
              <HeroImageViewer
                images={[mainImage, ...images]}
                isOpen={isModalOpen}
                toggleModal={toggleModal}
              />
            )}
            <div className="px-4 py-0 md:p-0">
              <HeroTitle
                title={title}
                slugToName={slugToName}
                category={{
                  categoryName: lvl0 && getSlugName(lvl0),
                  href: `${router.basePath}/${lvl0}`, // revisit and make sure it should be parent category(lvl0) or lvl2 ?? lvl1 ?? lvl0
                }}
              />
              <ProductMetaDetails
                className="mt-4"
                reviews={reviews}
                isCollectionProduct={isCollectionProduct}
                reviewsRef={reviewsRef}
                locations={locations}
                locationsRef={locationsMapRef}
              />
              <ProductPriceDetails
                displayPriceDetails={router?.query?.sp !== '0'}
                isCollectionChoice={isCollectionChoice}
                collectionLink={collectionUrl}
                onClickBackToCollection={handleClickBackToCollection}
                price={price}
                stock={stock}
                unitsAvailableIsPublic={unitsAvailableIsPublic}
                isActive={isActive}
                addToBasketComponent={
                  <AddToBasket
                    className="flex-1 [&_button]:m-0"
                    product={product}
                    isActive={isActive}
                    withQty={false}
                    flags={basketItemFlags}
                    quickBasket={true}
                    handleQuickBasket={() => setQuickBasketOpen(true)}
                  />
                }
                klarnaComponent={<Klarna price={price.displayPrice} />}
                stripeComponent={
                  <Stripe
                    productId={id}
                    productSlug={slug}
                    deliveryOptions={deliveryOptions}
                    displayPrice={price.displayPrice}
                  />
                }
                uspsComponent={<ProductUsps />}
              />
              <ProductOverview
                description={description}
                isCollectionProduct={isCollectionProduct}
                isWhatsIncludedVisible={!!additionalDetailsEntry?.isIntersecting}
                texts={texts}
                whatsIncluded={whatsIncluded}
                whatsIncludedRef={whatsIncludedRef}
                experienceSummaryComponent={
                  <ExperienceSummary
                    className="mt-10"
                    features={{
                      reviews,
                      sku,
                      promocode,
                      validity,
                      numberOfPeople,
                      locations,
                    }}
                    isCollectionProduct={isCollectionProduct}
                  />
                }
              />
            </div>
          </div>
        </Container>
      </QuickBasketContextProvider>
      <Container>
        <div className="dy-pdp-injection" />
      </Container>
      {!isCollectionProduct && reviewsList?.length > 0 ? (
        <div ref={reviewsRef} className="bg-background-page-faded">
          <Container>
            <Reviews
              averageRating={reviews?.averageRating}
              isPreviousVisible={!!additionalDetailsEntry?.isIntersecting}
              reviews={reviewsList}
              totalReviews={reviews?.count}
            />
          </Container>
        </div>
      ) : null}
      {!isCollectionProduct && locations?.length > 0 && (
        <Container>
          <div ref={locationsMapRef}>
            <Locations
              className="mx-auto my-10 mb-0 md:mt-20"
              id="locations"
              locations={locations}
              isLocationsVisible={!!locationsEntry?.isIntersecting}
              MapComponent={LocationsMap}
              GMAP_PUBLIC_API_KEY={process.env.NEXT_PUBLIC_GMAP_PUBLIC_API_KEY}
            />
          </div>
        </Container>
      )}
      {isCollectionProduct && Array.isArray(productChoices) ? (
        <Stack>
          <ProductChoices
            numProductChoices={productChoices?.length}
            productChoicesList={productChoices}
            baseURL={`/collection/${product.slug}`}
            onClick={handleClickProductChoice}
          />
        </Stack>
      ) : null}
      <Container>
        {!isCollectionChoice && (
          <AddToBasket
            className="mx-0 mb-14 mt-10 md:hidden [&_button]:m-0"
            product={product}
            withQty={false}
            isActive={isActive}
            flags={basketItemFlags}
          />
        )}
        {/* Others went on to buy module via DY */}
        <OthersWentOnToBuy />
        {categories?.length > 0 && (
          <MoreFromCategory
            className="mx-0 my-14 md:py-20"
            categories={categories.slice(0, 10)}
            slugNameMap={slugNameMap}
            title="More from: "
            homeLink={process.env.NEXT_PUBLIC_HOME_LINK.replace(/\/$/, '')}
          />
        )}
      </Container>
    </>
  );
};

export default Page;
